import React from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { CheckCircleOutline, HourglassEmpty } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RefundOverviewData, RefundStatus } from '../api';
import { formatRefundCardSuffix, formatRefundDate, useRefundAmountFormatter } from './refundFormat';

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
const AMOUNT_SX = { fontVariantNumeric: 'tabular-nums' };

type RefundsSectionProps = {
  overview: RefundOverviewData | undefined;
};

export const RefundsSection = ({ overview }: RefundsSectionProps) => {
  const { t } = useTranslation();
  const formatAmount = useRefundAmountFormatter();

  if (!overview?.refunds?.length) {
    return null;
  }

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={1} width="100%">
      <Typography variant="subtitle1" fontWeight={600}>
        {t('refunds.section.title')}
      </Typography>
      {overview.refunds.map((refund) => {
        const isPending = refund.status === RefundStatus.PENDING;
        return (
          <Grid key={refund.id} container flexDirection="column" flexWrap="nowrap" gap={0.25}>
            <Grid container justifyContent="space-between" flexWrap="nowrap" gap={1}>
              <Typography variant="body2" fontWeight={600}>
                {[refund.label ?? t(`refunds.entity.${refund.entity_type}`), formatRefundCardSuffix(refund.card_suffix)]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              <Typography variant="body2" fontWeight={600} dir="ltr" sx={AMOUNT_SX}>
                {formatAmount(refund.amount)}
              </Typography>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1}>
              <Typography variant="caption" color="text.secondary">
                {[formatRefundDate(refund.created_at, DATE_TIME_FORMAT), refund.reason_label, refund.created_by_name]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              <Grid container alignItems="center" flexWrap="nowrap" gap={0.5} width="auto">
                {isPending ? (
                  <HourglassEmpty sx={{ fontSize: 16, color: 'secondary.dark' }} />
                ) : (
                  <CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                <Typography variant="caption" color={isPending ? 'secondary.dark' : 'success.main'} noWrap>
                  {isPending ? t('refunds.section.statusPending') : t('refunds.section.statusRefunded')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
      <Divider />
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          {t('refunds.section.total')}
        </Typography>
        <Typography variant="body2" fontWeight={600} dir="ltr" sx={AMOUNT_SX}>
          {formatAmount(overview.refunded_total ?? 0)}
        </Typography>
      </Grid>
    </Grid>
  );
};
